import { useCallback, useEffect, useRef } from "react";

const CHAT_UNREAD_REFRESH_DELAY_MS = 2000;

export const useChatUnreadRefresh = ({
  getUnreadMessages,
  orderId,
  unreadMessagesCount,
  setUnreadMessagesCount,
}) => {
  const timeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  const cancelScheduledRefresh = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const refreshUnreadMessages = useCallback(async () => {
    if (orderId == null) return;

    const requestId = ++requestIdRef.current;

    try {
      const response = await getUnreadMessages(orderId);
      if (requestId === requestIdRef.current && response.status === 200) {
        setUnreadMessagesCount(response.data?.count || 0);
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error(error);
      }
    }
  }, [getUnreadMessages, orderId, setUnreadMessagesCount]);

  const scheduleUnreadRefresh = useCallback(() => {
    cancelScheduledRefresh();
    if (!unreadMessagesCount || orderId == null) return;

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      refreshUnreadMessages();
    }, CHAT_UNREAD_REFRESH_DELAY_MS);
  }, [cancelScheduledRefresh, orderId, refreshUnreadMessages, unreadMessagesCount]);

  const refreshUnreadOnClose = useCallback(() => {
    cancelScheduledRefresh();
    if (unreadMessagesCount) {
      refreshUnreadMessages();
    }
  }, [cancelScheduledRefresh, refreshUnreadMessages, unreadMessagesCount]);

  useEffect(
    () => () => {
      cancelScheduledRefresh();
      requestIdRef.current += 1;
    },
    [cancelScheduledRefresh, orderId],
  );

  return {
    refreshUnreadMessages,
    refreshUnreadOnClose,
    scheduleUnreadRefresh,
  };
};
